import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import InboxSearchDropdown, { InboxSearchChips } from "@/components/InboxSearchDropdown";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator,
  ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, ContextMenuCheckboxItem,
} from "@/components/ui/context-menu";
import useInboxSSE from "@/hooks/useInboxSSE";
import { toast } from "sonner";
import {
  Mail, MessageSquare, Star, Inbox as InboxIcon, Send, Archive, ArchiveX, ListTodo,
  Search, Pencil, ListPlus, ChevronLeft, ChevronRight, Tag,
  CornerUpLeft, CornerUpRight, Reply, ReplyAll, Forward, Trash2, ChevronDown, Paperclip, MoreHorizontal, Clock,
  Download, FileText, AtSign, Minus, X,
  AlertTriangle, ShoppingBag, Users as UsersIcon, Info, MessagesSquare, Percent, Bookmark
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import GmailConnect from "@/components/GmailConnect";
import SearchableSelect from "@/components/SearchableSelect";
import AddressBookInput from "@/components/AddressBookInput";
import RichEmailComposer from "@/components/RichEmailComposer";
import AttachmentPreview from "@/components/AttachmentPreview";
import QuickTagPicker from "@/components/QuickTagPicker";
import { useCompose } from "@/contexts/ComposeContext";
import useCached from "@/lib/useCached";
import { splitAddresses } from "@/lib/addresses";
import { decodeHtmlEntities } from "@/lib/sanitizeHtml";

const CHANNEL = { email: "Email", chat: "Chat" };

// Iter 71 — DOMPurify config used for every Gmail HTML body render.
// Centralised so the single-message and threaded views can't drift.
const HTML_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["a","b","i","em","strong","u","p","br","ul","ol","li","blockquote","span","div","table","thead","tbody","tr","td","th","h1","h2","h3","h4","h5","h6","img","hr","pre","code"],
  ALLOWED_ATTR: ["href","title","alt","src","target","style","width","height","colspan","rowspan"],
  FORBID_TAGS: ["script","style","iframe","object","embed","form","input","textarea","head","title","meta","link","html","body"],
  FORBID_CONTENTS: ["style","script","head","title","meta","link"],
  ADD_ATTR: ["target"],
  WHOLE_DOCUMENT: false,
};

const initials = (s) => (s || "?").split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const COLORS = ["bg-rose-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-teal-500", "bg-cyan-500", "bg-blue-500", "bg-indigo-500", "bg-violet-500", "bg-fuchsia-500", "bg-pink-500"];
const avatarColor = (s) => COLORS[(s || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${time} · ${dd}-${mm}-${yy}`;
};

// Iter 108 — Right-click sub-menu that lets the reviewer apply / remove
// Gmail labels from an inbox row without opening it. Sits inside the
// row ContextMenu (`Tag…` items already cover internal client/task tags).
// User labels only (system labels CATEGORY_*, INBOX, SENT etc. are
// hidden — those are exposed by the folder sidebar instead).
function RowLabelsSubmenu({ message, labels, gmailConnected, onToggle }) {
  const userLabels = useMemo(() => (labels || []).filter((L) => L.type === "user"), [labels]);
  const disabled = !gmailConnected || !message?.gmail_id;
  const applied = useMemo(() => new Set(message?.gmail_label_ids || []), [message]);
  if (disabled) {
    return (
      <ContextMenuItem
        disabled
        data-testid={`ctx-labels-disabled-${message.message_id}`}
      >
        <Tag className="h-3.5 w-3.5 mr-2"/>
        Labels…
        <span className="ml-auto text-[10px] text-slate-400">
          {!gmailConnected ? "Gmail off" : "Not synced"}
        </span>
      </ContextMenuItem>
    );
  }
  if (userLabels.length === 0) {
    return (
      <ContextMenuItem disabled data-testid={`ctx-labels-empty-${message.message_id}`}>
        <Tag className="h-3.5 w-3.5 mr-2"/>
        Labels…
        <span className="ml-auto text-[10px] text-slate-400">None</span>
      </ContextMenuItem>
    );
  }
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger
        data-testid={`ctx-labels-sub-${message.message_id}`}
      >
        <Tag className="h-3.5 w-3.5 mr-2"/>
        Labels
        <span className="ml-auto text-[10px] text-slate-400 font-mono">l</span>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent
        className="w-56 max-h-72 overflow-y-auto"
        data-testid={`ctx-labels-content-${message.message_id}`}
      >
        {userLabels.map((L) => {
          const on = applied.has(L.id);
          return (
            <ContextMenuCheckboxItem
              key={L.id}
              checked={on}
              onSelect={(e) => {
                // Keep the sub-menu open so the user can apply several
                // labels in a row (Gmail's UX).
                e.preventDefault();
                onToggle?.(message, L.id, L.name);
              }}
              data-testid={`ctx-label-${message.message_id}-${L.id}`}
            >
              <span className="truncate">{L.name}</span>
            </ContextMenuCheckboxItem>
          );
        })}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

// MessageLabelPicker — popover for applying/removing Gmail labels from the
// currently-open message. Pushes changes to Gmail via `/api/gmail/messages/.../labels`
// and signals the parent so the cached row + inbox list are refreshed.
function MessageLabelPicker({ message, labels, gmailConnected, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(false);
  const userLabels = useMemo(() => (labels || []).filter((L) => L.type === "user"), [labels]);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? userLabels.filter((L) => (L.name || "").toLowerCase().includes(s)) : userLabels;
  }, [userLabels, search]);
  if (!gmailConnected || !message?.gmail_id) return null;
  const applied = new Set(message.gmail_label_ids || []);
  const toggle = async (labelId) => {
    if (pending) return;
    setPending(true);
    const wasApplied = applied.has(labelId);
    try {
      const r = await api.post(`/gmail/messages/${message.gmail_id}/labels`, wasApplied
        ? { remove: [labelId] }
        : { add: [labelId] });
      onUpdated?.(r.data?.label_ids || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update label.");
    } finally {
      setPending(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="msg-label-picker"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50"
          title="Apply Gmail labels"
        ><Tag className="h-3 w-3"/>Labels</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="px-3 pt-2.5 pb-2 border-b border-slate-100">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter labels"
            className="h-7 text-xs"
            data-testid="msg-label-search"
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-400 italic">No labels match.</div>
          ) : filtered.map((L) => {
            const on = applied.has(L.id);
            return (
              <button
                key={L.id}
                type="button"
                onClick={() => toggle(L.id)}
                disabled={pending}
                data-testid={`msg-label-${L.id}`}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-slate-50 ${on ? "text-slate-900" : "text-slate-600"}`}
              >
                <Checkbox checked={on} className="pointer-events-none"/>
                <span className="truncate">{L.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Hierarchical Gmail label tree. Parses Gmail's `Parent/Child` naming
// convention and renders a collapsible tree. System labels are filtered out
// (they're already in the top folder list); CATEGORY_* labels are hidden.
function GmailLabelTree({ labels, active, onPick }) {
  const userLabels = useMemo(() => (labels || []).filter((L) => L.type === "user"), [labels]);
  // Build a path tree
  const tree = useMemo(() => {
    const root = { children: {}, label: null };
    for (const L of userLabels) {
      const segments = (L.name || "").split("/");
      let node = root;
      let path = "";
      segments.forEach((seg, i) => {
        path = path ? `${path}/${seg}` : seg;
        node.children[seg] = node.children[seg] || { children: {}, label: null, path };
        node = node.children[seg];
        if (i === segments.length - 1) node.label = L;
      });
    }
    return root;
  }, [userLabels]);
  const [open, setOpen] = useState(() => new Set());
  if (!userLabels.length) return null;
  const renderNode = (node, name, depth) => {
    const isOpen = open.has(node.path);
    const hasChildren = Object.keys(node.children).length > 0;
    const id = node.label?.id;
    const isActive = id && active === `gmail:${id}`;
    // Iter 110ac — Render the coloured Gmail label chip that the user set
    // in Gmail itself (backgroundColor is the fill, textColor is unused —
    // we don't render the label text on top of the swatch).  Fallback to
    // a neutral tag icon when no colour is set.
    const swatchBg = node.label?.color?.backgroundColor;
    return (
      <div key={node.path}>
        <button
          type="button"
          data-testid={`gmail-label-${node.path}`}
          onClick={() => {
            if (id) onPick(id);
            if (hasChildren) {
              setOpen((prev) => {
                const next = new Set(prev);
                if (next.has(node.path)) next.delete(node.path); else next.add(node.path);
                return next;
              });
            }
          }}
          className={`w-full flex items-center gap-1.5 pr-2 py-1.5 text-[12px] rounded-md transition-colors ${
            isActive ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-600 hover:bg-slate-100"
          }`}
          style={{ paddingLeft: `${10 + depth * 12}px` }}
        >
          {hasChildren ? (
            isOpen ? <ChevronDown className="h-3 w-3 text-slate-400 shrink-0"/> : <ChevronRight className="h-3 w-3 text-slate-400 shrink-0"/>
          ) : (
            swatchBg ? (
              <span
                className="h-3 w-3 shrink-0 inline-block rounded-[3px]"
                style={{ backgroundColor: swatchBg, border: "1px solid rgba(0,0,0,0.08)" }}
                data-testid={`gmail-label-swatch-${node.path}`}
              />
            ) : <Tag className="h-3 w-3 text-slate-400 shrink-0"/>
          )}
          <span className="truncate text-left flex-1">{name}</span>
        </button>
        {hasChildren && isOpen && (
          <div>{Object.entries(node.children).map(([n, c]) => renderNode(c, n, depth + 1))}</div>
        )}
      </div>
    );
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 pr-2">
      <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Labels</div>
      {Object.entries(tree.children).map(([n, c]) => renderNode(c, n, 0))}
    </div>
  );
}

// Iter 110ac — Gmail native "Categories" section mirror (Personal /
// Promotions / Social / Updates / Forums / Purchases). Collapsible sub-list
// so the sidebar stays quiet when the user doesn't use categories. Each
// item maps to the CATEGORY_* system label id which the backend filter
// (`gmail_label_id=`) already supports.
const GMAIL_CATEGORIES = [
  { id: "CATEGORY_PERSONAL", label: "Personal", icon: UsersIcon },
  { id: "CATEGORY_PURCHASES", label: "Purchases", icon: ShoppingBag },
  { id: "CATEGORY_SOCIAL", label: "Social", icon: UsersIcon },
  { id: "CATEGORY_UPDATES", label: "Updates", icon: Info },
  { id: "CATEGORY_FORUMS", label: "Forums", icon: MessagesSquare },
  { id: "CATEGORY_PROMOTIONS", label: "Promotions", icon: Percent },
];
function CategoriesGroup({ active, onPick }) {
  const [open, setOpen] = useState(false);
  const isCategoryActive = (active || "").startsWith("gmail:CATEGORY_");
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="folder-categories"
        className={`w-full flex items-center gap-3 pl-6 pr-4 py-2 rounded-r-full text-sm transition-colors ${
          isCategoryActive ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400"/> : <ChevronRight className="h-3.5 w-3.5 text-slate-400"/>}
        <Tag className="h-4 w-4 text-slate-500" strokeWidth={1.8}/>
        <span className="flex-1 text-left">Categories</span>
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {GMAIL_CATEGORIES.map((c) => {
            const id = `gmail:${c.id}`;
            const isActive = active === id;
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onPick(c.id)}
                data-testid={`folder-category-${c.id}`}
                className={`w-full flex items-center gap-3 pl-12 pr-4 py-1.5 rounded-r-full text-[13px] transition-colors ${
                  isActive ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.8}/>
                <span className="flex-1 text-left">{c.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Triage() {
  const { user: me } = useAuth();
  const [folder, setFolder] = useState("inbox"); // inbox | starred | sent | archived | all
  const [search, setSearch] = useState("");
  const [messages, setMessages] = useState([]);
  // Iter 69 — initial-load tri-state: `null` = haven't fetched yet,
  // `true` = fetch in flight, `false` = fetched. Used to suppress the
  // "No messages in this folder" flash while the first request lands.
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const composeApi = useCompose();
  // Iter 67 — In-app attachment preview overlay.
  const [previewAttachment, setPreviewAttachment] = useState(null); // {att, gmail_id}
  const [todoOpen, setTodoOpen] = useState(false);
  const [todo, setTodo] = useState({ title: "", notes: "", due_date: "", assigned_user_id: "" });
  const [counts, setCounts] = useState({ inbox: 0, starred: 0, sent: 0, archived: 0 });
  const [gmailConnected, setGmailConnected] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [gmailLabels, setGmailLabels] = useState([]);
  const [scheduledSends, setScheduledSends] = useState([]);
  // Iter 67 — Drafts: list & current draft id for autosave linkage.
  const [drafts, setDrafts] = useState([]);

  const loadDrafts = useCallback(() => {
    api.get("/drafts").then((r) => setDrafts(r.data?.drafts || [])).catch(() => {});
  }, []);
  // Iter 93 — gmail/config + labels are navigation-static: a couple
  // dozen labels that change only when the user adds one. Serve from
  // SWR cache so the second/third page-visit is instant.
  const { data: cachedGmailCfg } = useCached("/gmail/config", { ttl: 120_000 });
  useEffect(() => {
    if (!cachedGmailCfg) return;
    setGmailConnected(!!cachedGmailCfg.connected);
    setConnectedGmailEmail((cachedGmailCfg.connected_email || "").toLowerCase());
  }, [cachedGmailCfg]);
  const { data: cachedGmailLabels, error: gmailLabelsError, refresh: refreshGmailLabels } = useCached(
    gmailConnected ? "/gmail/labels" : null,
    { ttl: 120_000 },
  );
  useEffect(() => {
    if (cachedGmailLabels?.labels?.length) setGmailLabels(cachedGmailLabels.labels);
  }, [cachedGmailLabels]);
  // Iter 110e — Belt-and-braces labels loader. Some prod users reported
  // the sidebar Labels section never appearing even though `/gmail/labels`
  // returns successfully when called directly. Root cause traced to a
  // poisoned `useCached` entry in `sessionStorage` from an earlier failed
  // fetch: empty-array responses were being written through, the cached
  // entry was hit on next mount, and the late-arriving real response
  // never repopulated state because the useEffect above guarded on
  // `cachedGmailLabels?.labels` (truthy for `[]`) but never re-triggered
  // on subsequent identical cache writes.
  //
  // This direct fetch fires on every Triage mount once `gmailConnected`
  // is known. It bypasses the SWR layer entirely so the labels surface
  // can't be left in a stale-empty state. Runs in parallel with the
  // useCached path so the cache benefit is preserved for instant
  // navigation; whichever resolves first wins.
  useEffect(() => {
    if (!gmailConnected) return;
    let cancelled = false;
    api.get("/gmail/labels")
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.labels || [];
        if (list.length) setGmailLabels(list);
      })
      .catch(() => { /* useCached error handler shows the retry toast */ });
    return () => { cancelled = true; };
  }, [gmailConnected]);
  // Iter 108b — Production users were silently getting an empty
  // gmailLabels list when the live Gmail call failed (token refresh /
  // rate-limit / transient 5xx). Surface the failure with a toast +
  // auto-retry once after a 4s back-off so the sidebar tree comes back
  // on the next render. Backend now also falls back to its MongoDB
  // mirror — together this should make the "labels disappeared" case
  // self-heal.
  useEffect(() => {
    if (!gmailLabelsError) return;
    const t = setTimeout(() => { refreshGmailLabels?.(); }, 4000);
    if (gmailLabels.length === 0) {
      toast.error("Couldn't load Gmail labels — retrying…", { id: "gmail-labels-retry" });
    }
    return () => clearTimeout(t);
  }, [gmailLabelsError, refreshGmailLabels, gmailLabels.length]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("inbox.viewMode") || "split"; } catch { return "split"; }
  }); // "list" | "split"
  const setViewModePersist = (m) => {
    setViewMode(m);
    try { localStorage.setItem("inbox.viewMode", m); } catch {}
  };
  // Folder may be `gmail:<labelId>` to scope to a Gmail label (system or user).
  const isGmailFolder = (folder || "").startsWith("gmail:");
  const gmailFolderId = isGmailFolder ? folder.slice(6) : null;

  // Iter 70 — Cache the user's connected Gmail address so we can apply
  // the identity filter on Reply/Reply-All (never To/CC the user back to
  // themselves). Stored as a ref so the filter logic inside `openReply`
  // doesn't have to wait on a re-render.
  const [connectedGmailEmail, setConnectedGmailEmail] = useState("");

  // Iter 93 — gmail/config + labels are driven by `useCached` higher up.
  // The previous direct-fetch effects were removed to avoid double network
  // hits on every navigation.

  // Pending scheduled sends — refresh on Gmail connect + every 60s while open.
  const loadScheduled = useCallback(() => {
    if (!gmailConnected) return;
    api.get("/gmail/scheduled")
      .then((r) => setScheduledSends(r.data?.scheduled || []))
      .catch(() => {});
  }, [gmailConnected]);
  useEffect(() => {
    loadScheduled();
    if (!gmailConnected) return;
    const id = setInterval(loadScheduled, 60000);
    return () => clearInterval(id);
  }, [gmailConnected, loadScheduled]);
  const cancelScheduled = async (sid) => {
    try {
      await api.delete(`/gmail/scheduled/${sid}`);
      toast.success("Scheduled send cancelled.");
      loadScheduled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel.");
    }
  };

  const active = useMemo(() => messages.find((m) => m.message_id === activeId), [messages, activeId]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (folder === "archived") params.set("status", "archived");
    // Iter 71 — Batch C — Detect Gmail operators in the search box and
    // pass them via `gmail_q` so the backend pipes them straight to
    // Gmail's native search (supports has:attachment, from:, to:, is:,
    // subject:, label:, before:, after:, etc.).
    const isGmailOperatorQuery = !!search && /\b(from|to|cc|bcc|subject|has|is|label|before|after|older_than|newer_than|filename|in|category|larger|smaller|deliveredto|rfc822msgid)\s*:/i.test(search);
    if (search && !isGmailOperatorQuery) params.set("q", search);
    if (isGmailOperatorQuery) params.set("gmail_q", search);
    if (isGmailFolder && gmailFolderId) params.set("gmail_label_id", gmailFolderId);
    // Iter 48 — `light=true` returns metadata only (no body / html / attachments).
    // The body is fetched on row click via GET /api/inbox/{id}/body.
    params.set("light", "true");
    setMessagesLoading(true);
    let r;
    try {
      r = await api.get(`/inbox?${params.toString()}`);
    } finally {
      setMessagesLoading(false);
    }
    let list = r.data || [];

    // Folder filter (client-side, since folders aren't true backend buckets).
    // Iter 43 — Draft Gmail rows are now excluded at the backend level too,
    // but keep a client-side guard for cached rows that pre-date the filter.
    const isDraft = (m) => Array.isArray(m.gmail_label_ids) && m.gmail_label_ids.includes("DRAFT");
    list = list.filter((m) => !isDraft(m));
    if (folder === "inbox") list = list.filter((m) => m.direction === "inbound" && m.status !== "archived");
    else if (folder === "sent") list = list.filter((m) => m.direction === "outbound");
    else if (folder === "starred") list = list.filter((m) => m.starred);
    else if (folder === "all") list = list.filter((m) => m.status !== "archived");
    // Gmail-label folders use the backend filter directly; nothing to refine here.

    setMessages(list);

    // Recompute folder counts based on master list
    const all = (r.data || []).filter((m) => !isDraft(m));
    setCounts({
      inbox: all.filter((m) => m.direction === "inbound" && m.status === "unread").length,
      starred: all.filter((m) => m.starred && m.status !== "archived").length,
      sent: all.filter((m) => m.direction === "outbound").length,
      archived: all.filter((m) => m.status === "archived").length,
    });
  }, [folder, search, me?.user_id]);

  // Navigation-static lookups: use stale-while-revalidate cache so we
  // don't re-hit the network every time we land on this page. Shows
  // last-known data instantly, refreshes in background.
  const { data: cachedClients } = useCached("/clients", { ttl: 60_000 });
  const { data: cachedUsers } = useCached("/users", { ttl: 60_000 });
  useEffect(() => { if (cachedClients) setClients(cachedClients); }, [cachedClients]);
  useEffect(() => { if (cachedUsers) setUsers(cachedUsers); }, [cachedUsers]);
  useEffect(() => { load(); }, [load]);

  // Iter 48 — Cache-first: when the user opens a message we only have
  // metadata locally. Lazy-fetch the body & body_html and merge into the
  // local copy so subsequent re-renders don't re-fetch.
  const ensureBodyLoaded = useCallback(async (m) => {
    if (!m) return m;
    if (m.body_html !== undefined || m.body !== undefined) return m;
    try {
      const r = await api.get(`/inbox/${m.message_id}/body`);
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, ...r.data } : x
      ));
      // Iter 82 — return the merged message so callers (e.g. openReply on
      // forward) can use the freshly-loaded attachments WITHOUT having to
      // wait for the state closure to refresh on the next render. Without
      // this, forwarding the very first email of a session shipped zero
      // attachments because `messages.find(...)` still saw the pre-load
      // version of the row.
      return { ...m, ...r.data };
    } catch (e) {
      console.warn("[triage] failed to lazy-load body", e);
      return m;
    }
  }, []);

  // Iter 48 — SSE listener. Server pushes `email.new` (a new inbound
  // arrives), `email.updated` (read/star/archive triggered from another
  // tab), `email.deleted`. We patch the local cache in place so the user
  // doesn't see a flicker.
  //
  // Iter 49 fix — `email.new` was previously pushing every freshly synced
  // row onto the list regardless of folder/direction. The history-sync
  // job broadcasts BOTH inbound and outbound rows (a user-sent email is
  // labelled SENT only, but Gmail still emits a history event for it),
  // so e.g. "Sales reconciliation" outbound rows were appearing in the
  // Inbox folder. We now mirror the folder/direction filter used by
  // `load()` so SSE rows respect the active view.
  const folderRef = useRef(folder);
  useEffect(() => { folderRef.current = folder; }, [folder]);
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  useInboxSSE(apiBase, (event) => {
    if (event.type === "email.new_batch" && Array.isArray(event.items)) {
      // Iter 110ah — Batched frame from big backfills. Backend collapses
      // 500 individual `email.new` frames into a single `email.new_batch`
      // so we do ONE setMessages call instead of 500 (avoids React
      // reconciliation storm). Filter each item through the same
      // folder-belongs / dedupe / unread-count logic as the singleton
      // handler below.
      const f = folderRef.current;
      const fresh = [];
      let unreadDelta = 0;
      for (const m of event.items) {
        const isDraft = Array.isArray(m.gmail_label_ids) && m.gmail_label_ids.includes("DRAFT");
        if (isDraft) continue;
        let belongs = true;
        if (f === "inbox") belongs = m.direction === "inbound" && m.status !== "archived";
        else if (f === "sent") belongs = m.direction === "outbound";
        else if (f === "starred") belongs = !!m.starred;
        else if (f === "archived") belongs = m.status === "archived";
        else if (f === "all") belongs = m.status !== "archived";
        if (!belongs) continue;
        fresh.push(m);
        if (m.direction === "inbound" && m.status === "unread") unreadDelta += 1;
      }
      if (fresh.length > 0) {
        setMessages((prev) => {
          const existing = new Set(prev.map((x) => x.message_id));
          const uniq = fresh.filter((m) => !existing.has(m.message_id));
          return uniq.length ? [...uniq, ...prev] : prev;
        });
        if (unreadDelta) setCounts((c) => ({ ...c, inbox: c.inbox + unreadDelta }));
      }
    } else if (event.type === "email.new" && event.patch) {
      const m = event.patch;
      const isDraft = Array.isArray(m.gmail_label_ids) && m.gmail_label_ids.includes("DRAFT");
      if (isDraft) return;
      const f = folderRef.current;
      let belongs = true;
      if (f === "inbox") belongs = m.direction === "inbound" && m.status !== "archived";
      else if (f === "sent") belongs = m.direction === "outbound";
      else if (f === "starred") belongs = !!m.starred;
      else if (f === "archived") belongs = m.status === "archived";
      else if (f === "all") belongs = m.status !== "archived";
      if (!belongs) return;
      setMessages((prev) => {
        if (prev.some((x) => x.message_id === m.message_id)) return prev;
        return [m, ...prev];
      });
      if (m.direction === "inbound" && m.status === "unread") {
        setCounts((c) => ({ ...c, inbox: c.inbox + 1 }));
      }
    } else if (event.type === "email.updated" && event.patch) {
      setMessages((prev) => prev.map((m) =>
        m.message_id === event.message_id ? { ...m, ...event.patch } : m
      ));
    } else if (event.type === "email.deleted") {
      setMessages((prev) => prev.filter((m) => m.message_id !== event.message_id));
      setActiveId((id) => (id === event.message_id ? null : id));
    }
  });

  const openMessage = async (m) => {
    setActiveId(m.message_id);
    // Lazy-load body (Iter 48). Independent of the read-receipt patch
    // below so the body shows up even if the row is already read.
    ensureBodyLoaded(m);
    if (m.status === "unread") {
      // Optimistic — flip locally first, then call backend.
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, status: "read" } : x
      ));
      try {
        await api.patch(`/inbox/${m.message_id}`, { status: "read" });
      } catch {
        // Roll back on failure.
        setMessages((prev) => prev.map((x) =>
          x.message_id === m.message_id ? { ...x, status: "unread" } : x
        ));
        toast.error("Couldn't mark as read");
      }
    }
  };

  // Iter 48 — Optimistic mutations. Phase 1: flip local state with
  // `_pending` flag. Phase 2: fire the API call. Phase 3: clear pending
  // on success, roll back on failure.
  const toggleStar = async (m, e) => {
    if (e) e.stopPropagation();
    const next = !m.starred;
    setMessages((prev) => prev.map((x) =>
      x.message_id === m.message_id ? { ...x, starred: next, _pending: true } : x
    ));
    try {
      await api.patch(`/inbox/${m.message_id}`, { starred: next });
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, _pending: false } : x
      ));
    } catch (err) {
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, starred: !next, _pending: false } : x
      ));
      toast.error("Couldn't update star");
    }
  };

  const archiveMsg = async (m) => {
    const wasArchived = m.status === "archived";
    const nextStatus = wasArchived ? "read" : "archived";
    // Optimistic — remove from current view if archiving from Inbox.
    setMessages((prev) => prev.map((x) =>
      x.message_id === m.message_id ? { ...x, status: nextStatus, _pending: true } : x
    ));
    if (!wasArchived && activeId === m.message_id) setActiveId(null);
    toast.success(wasArchived ? "Moved to Inbox" : "Archived");
    try {
      await api.patch(`/inbox/${m.message_id}`, { status: nextStatus });
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, _pending: false } : x
      ));
      // Background refresh for counts only — no flicker since SSE handles
      // the per-row patching.
      load();
    } catch (err) {
      // Roll back.
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, status: m.status, _pending: false } : x
      ));
      toast.error("Couldn't archive");
    }
  };

  const deleteMsg = async (m) => {
    // Iter 43 — no confirmation popup.
    // Iter 48 — Optimistic remove with rollback.
    const snapshot = m;
    setMessages((prev) => prev.filter((x) => x.message_id !== m.message_id));
    if (activeId === m.message_id) setActiveId(null);
    toast.success("Deleted");
    try {
      await api.delete(`/inbox/${m.message_id}`);
    } catch (e) {
      // Restore the row at the top so the user can retry.
      setMessages((prev) => [snapshot, ...prev]);
      toast.error(e?.response?.data?.detail || "Couldn't delete — restored");
    }
  };

  // Iter 108 — Apply / remove a Gmail label from any inbox row via the
  // right-click context menu. Mirrors the behaviour of the detail-pane
  // `MessageLabelPicker` but is keyed off the row's own message data, so
  // users can label messages without first opening them.
  const toggleRowLabel = async (m, labelId, labelName) => {
    if (!m?.gmail_id) {
      toast.error("This message isn't synced to Gmail yet.");
      return;
    }
    const current = new Set(m.gmail_label_ids || []);
    const wasApplied = current.has(labelId);
    const body = wasApplied ? { remove: [labelId] } : { add: [labelId] };
    // Optimistic patch.
    const optimistic = wasApplied
      ? (m.gmail_label_ids || []).filter((x) => x !== labelId)
      : [...(m.gmail_label_ids || []), labelId];
    setMessages((prev) => prev.map((x) =>
      x.message_id === m.message_id ? { ...x, gmail_label_ids: optimistic } : x
    ));
    try {
      const r = await api.post(`/gmail/messages/${m.gmail_id}/labels`, body);
      const newIds = r.data?.label_ids || optimistic;
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, gmail_label_ids: newIds } : x
      ));
      toast.success(wasApplied ? `Removed "${labelName}"` : `Labelled "${labelName}"`);
    } catch (e) {
      // Roll back.
      setMessages((prev) => prev.map((x) =>
        x.message_id === m.message_id ? { ...x, gmail_label_ids: m.gmail_label_ids || [] } : x
      ));
      toast.error(e?.response?.data?.detail || "Failed to update label.");
    }
  };

  // Iter 84 — `addToTodo` (silent variant) removed; the dialog-based
  // `openTodoFromMessage` is now the single source of truth. The
  // duplicate top-toolbar button has been removed too.

  // Iter 51 — Thread grouping. Gmail collapses every message that shares
  // a `thread_id` into a single inbox row showing the LATEST message;
  // count chip indicates how many in the conversation. We keep the
  // expanded list available via `threadMessages` (last 50 of the thread)
  // so clicking the count chip can later expose them in a side panel.
  const threadGroups = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const key = m.thread_id || m.message_id;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { head: m, all: [m] });
      } else {
        existing.all.push(m);
        if ((m.received_at || "") > (existing.head.received_at || "")) {
          existing.head = m;
        }
      }
    }
    const out = [];
    for (const grp of map.values()) {
      out.push({
        ...grp.head,
        _thread_count: grp.all.length,
        _thread_unread: grp.all.some((x) => x.status === "unread"),
        _thread_starred: grp.all.some((x) => x.starred),
      });
    }
    out.sort((a, b) => (b.received_at || "").localeCompare(a.received_at || ""));
    return out;
  }, [messages]);

  // Iter 110r — Render the "to ..." line on a message detail header
  // Gmail-style: list every recipient with the current user's address
  // collapsed to "me", and other recipients shown by display name (or
  // email if no name available). The previous render hard-coded "me"
  // for any inbound message — so if the email was actually addressed
  // to me + two colleagues, the header silently misled the reader into
  // thinking they were the sole recipient.
  //
  // Returns "me" / "Aarti, me" / "Aarti, Bhimal, me" / for outbound:
  // each recipient's display name comma-joined.
  const formatRecipientsForHeader = (rawList, direction) => {
    const labels = _labelsForHeader(rawList, direction, me?.email);
    if (labels.length === 0) return direction === "outbound" ? "(no recipient)" : "me";
    return labels.join(", ");
  };

  // Iter 55 — Thread chain. When the user opens an email row that's
  // part of a multi-message thread, surface the entire chain in the
  // detail pane: older messages collapsed (sender · date · snippet) at
  // the top, the most recent one expanded with full body.
  const activeThread = useMemo(() => {
    if (!active) return [];
    const tid = active.thread_id || active.message_id;
    // Iter 93k — Front/Superhuman ordering: newest message at the TOP
    // (descending chronological) so users see the latest action without
    // scrolling. The oldest root message sits at the bottom.
    return messages
      .filter((m) => (m.thread_id || m.message_id) === tid)
      .sort((a, b) => (b.received_at || "").localeCompare(a.received_at || ""));
  }, [active, messages]);

  // Iter 93k — classify each message into the four action types the
  // Phase 1 schema specifies. Derived client-side from existing data
  // (direction + subject + in_reply_to_header) so we don't need a
  // backend migration. Drives the reply/forward icon on each thread
  // header. Result: 'inbound' | 'outbound_reply' | 'outbound_forward'
  // | 'outbound_new'.
  const actionTypeOf = useCallback((m) => {
    if (!m) return "inbound";
    if (m.direction !== "outbound") return "inbound";
    if (/^\s*fwd?\s*:/i.test(m.subject || "")) return "outbound_forward";
    if ((m.in_reply_to_header || "").trim() || /^\s*re\s*:/i.test(m.subject || "")) return "outbound_reply";
    return "outbound_new";
  }, []);

  const [expandedThreadIds, setExpandedThreadIds] = useState(() => new Set());
  // Iter 71 — Batch E — "Show trimmed content" toggle. Tracks message_ids
  // whose quoted reply chain should be rendered. Matches Gmail's "..."
  // dot-puff button — collapsed by default so the visible body shows only
  // the new content.
  const [expandedQuotes, setExpandedQuotes] = useState(() => new Set());
  const toggleQuote = useCallback((mid) => {
    setExpandedQuotes((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  }, []);
  // Whenever the active conversation changes, default to only the latest
  // message expanded. After Iter 93k the newest message sits at index 0
  // (descending order), so we expand that one.
  useEffect(() => {
    if (active && activeThread.length) {
      const latest = activeThread[0];
      setExpandedThreadIds(new Set([latest.message_id]));
    } else {
      setExpandedThreadIds(new Set());
    }
  }, [active?.message_id, activeThread.length]);
  const toggleThreadMessage = useCallback(async (m) => {
    setExpandedThreadIds((prev) => {
      const next = new Set(prev);
      if (next.has(m.message_id)) next.delete(m.message_id);
      else next.add(m.message_id);
      return next;
    });
    // Lazy-load body the first time this older message is expanded.
    if (m.body_html === undefined && m.body === undefined) {
      try {
        const r = await api.get(`/inbox/${m.message_id}/body`);
        setMessages((prev) => prev.map((x) =>
          x.message_id === m.message_id ? { ...x, ...r.data } : x
        ));
      } catch (e) { /* swallow */ }
    }
  }, []);

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Iter 71 — Batch C — Shift+click multi-select. Tracks the last-clicked
  // row index; on shift+click we select the contiguous range between
  // last and current (Gmail / native finder behaviour).
  const lastSelectedIndexRef = useRef(-1);
  const handleRowSelect = (id, index, e) => {
    if (e?.shiftKey && lastSelectedIndexRef.current >= 0) {
      const a = Math.min(lastSelectedIndexRef.current, index);
      const b = Math.max(lastSelectedIndexRef.current, index);
      setSelected((prev) => {
        const next = new Set(prev);
        for (let i = a; i <= b; i++) {
          const row = threadGroups[i];
          if (row) next.add(row.message_id);
        }
        return next;
      });
    } else {
      toggleSelected(id);
      lastSelectedIndexRef.current = index;
    }
  };
  const selectAll = () => {
    if (selected.size === threadGroups.length) setSelected(new Set());
    else setSelected(new Set(threadGroups.map((m) => m.message_id)));
  };

  // Iter 71 — Batch C — Bulk actions backed by Gmail's native
  // `batchModify` API (one round-trip for up to 1000 messages). Falls
  // back to per-row PATCH for non-Gmail rows or when Gmail isn't
  // connected.
  const _gmailIdsFor = (ids) => {
    const set = new Set(ids);
    return messages.filter((m) => set.has(m.message_id) && m.gmail_id);
  };
  const bulkGmailModify = async (add, remove) => {
    const ids = Array.from(selected);
    if (!ids.length) return 0;
    const gmailRows = _gmailIdsFor(ids);
    if (gmailConnected && gmailRows.length) {
      try {
        await api.post("/gmail/batch-modify", {
          message_ids: gmailRows.map((r) => r.message_id),
          add_label_ids: add,
          remove_label_ids: remove,
        });
      } catch (e) {
        console.warn("[triage] batchModify failed", e);
      }
    }
    return gmailRows.length;
  };
  const bulkArchive = async () => {
    const count = selected.size;
    if (!count) return;
    // Gmail: removing INBOX label = archive. We also patch local rows.
    await bulkGmailModify([], ["INBOX"]);
    for (const id of selected) {
      try { await api.patch(`/inbox/${id}`, { status: "archived" }); } catch { /* swallow */ }
    }
    setSelected(new Set());
    toast.success(`${count} archived`);
    load();
  };
  const bulkMarkRead = async (status = "read") => {
    const count = selected.size;
    if (!count) return;
    // Gmail: UNREAD label toggle. When marking read → remove UNREAD; when
    // marking unread → add UNREAD.
    if (status === "read") await bulkGmailModify([], ["UNREAD"]);
    else await bulkGmailModify(["UNREAD"], []);
    for (const id of selected) {
      try { await api.patch(`/inbox/${id}`, { status }); } catch { /* swallow */ }
    }
    setSelected(new Set());
    toast.success(`${count} marked as ${status}`);
    load();
  };
  const bulkDelete = async () => {
    const count = selected.size;
    if (!count) return;
    if (!window.confirm(`Delete ${count} message${count === 1 ? "" : "s"}?`)) return;
    // Gmail: adding TRASH label moves to Trash; our delete endpoint also
    // wipes the local row. Run both so Gmail and our cache stay aligned.
    await bulkGmailModify(["TRASH"], ["INBOX"]);
    for (const id of selected) {
      try { await api.delete(`/inbox/${id}`); } catch { /* swallow */ }
    }
    setSelected(new Set());
    toast.success(`${count} deleted`);
    load();
  };

  // Iter 110ab — MERGE the PATCH response into the existing row, but
  // EXCLUDE body / body_html / body_html_quoted / attachments so the row's
  // lazy-loaded (and quote-split) presentation copy isn't clobbered by the
  // raw persisted HTML that the PATCH endpoint returns. The previous
  // implementation used a full replace (`? r.data : x`) which silently
  // wiped `body_html_quoted` and rehydrated `body_html` with the unsplit
  // Mongo copy, visually showing quoted content that had been trimmed
  // seconds earlier when the user opened the message.
  const _mergePatchIntoRow = (row, patchDoc) => {
    const clean = { ...patchDoc };
    delete clean.body;
    delete clean.body_html;
    delete clean.body_html_quoted;
    delete clean.attachments;
    return { ...row, ...clean };
  };
  const linkClient = async (cid, target) => {
    const msg = target || active;
    if (!msg) return;
    const r = await api.patch(`/inbox/${msg.message_id}`, {
      client_id: cid === "none" ? null : cid,
      // Reset period & task when client changes
      period_id: null,
      task_id: null,
    });
    setMessages((prev) => prev.map((x) => x.message_id === msg.message_id ? _mergePatchIntoRow(x, r.data) : x));
  };
  const linkPeriod = async (pid, target) => {
    const msg = target || active;
    if (!msg) return;
    const r = await api.patch(`/inbox/${msg.message_id}`, {
      period_id: pid === "none" ? null : pid,
      task_id: null,
    });
    setMessages((prev) => prev.map((x) => x.message_id === msg.message_id ? _mergePatchIntoRow(x, r.data) : x));
  };
  const linkTask = async (tid, target) => {
    const msg = target || active;
    if (!msg) return;
    const r = await api.patch(`/inbox/${msg.message_id}`, { task_id: tid === "none" ? null : tid });
    setMessages((prev) => prev.map((x) => x.message_id === msg.message_id ? _mergePatchIntoRow(x, r.data) : x));
  };
  const assignUser = async (uid) => {
    if (!active) return;
    const r = await api.patch(`/inbox/${active.message_id}`, { assigned_user_id: uid === "none" ? null : uid });
    setMessages((prev) => prev.map((x) => x.message_id === active.message_id ? _mergePatchIntoRow(x, r.data) : x));
  };

  // ---------------------------------------------------------------
  // Iter 94 — Quick Tagging (Clients / Closes / Tasks) from the inbox.
  //
  // Two entry points wire into the SAME `QuickTagPicker` Dialog so the
  // UX stays consistent:
  //   (A) Keyboard shortcuts on the currently-active message:
  //       `c` → tag client, `p` → tag close (period), `t` → tag task.
  //       Letters are ignored when the user is typing into an input,
  //       textarea, or contentEditable element (so subject editing and
  //       the email composer keep working).
  //   (D) Right-click context menu items on every inbox row (works on
  //       both the active row and any other row in the list).
  //
  // Periods/tasks for non-active rows are fetched on demand when the
  // picker opens. Clients are already cached app-wide via `useCached`.
  // ---------------------------------------------------------------
  const [quickTag, setQuickTag] = useState(null); // { kind, message } | null
  const [quickPeriods, setQuickPeriods] = useState([]);
  const [quickTasks, setQuickTasks] = useState([]);

  const openQuickTag = useCallback((kind, message) => {
    if (!message) return;
    if (kind === "period" && !message.client_id) {
      toast.message("Tag a client first to pick a close.");
      return;
    }
    if (kind === "task" && !message.period_id) {
      toast.message("Tag a close first to pick a task.");
      return;
    }
    setQuickTag({ kind, message });
  }, []);

  // Lazy-load periods / tasks when the picker opens for a row that
  // isn't currently active (so the cached `periods` / `tasks` arrays
  // wouldn't match the right scope).
  useEffect(() => {
    if (!quickTag) return;
    const { kind, message } = quickTag;
    if (kind === "period" && message?.client_id) {
      api.get(`/periods?client_id=${message.client_id}`)
        .then((r) => setQuickPeriods(r.data || []))
        .catch(() => setQuickPeriods([]));
    } else if (kind === "task" && message?.period_id) {
      api.get(`/periods/${message.period_id}/tasks`)
        .then((r) => setQuickTasks(r.data || []))
        .catch(() => setQuickTasks([]));
    }
  }, [quickTag]);

  // Keyboard shortcuts: c / p / t when an active message is open AND
  // the user is not typing into any input / textarea / contentEditable.
  useEffect(() => {
    const handler = (e) => {
      // Modifier-bearing keys (Cmd+T new tab, Ctrl+P print, etc.) must
      // pass through untouched.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (t?.isContentEditable) return;
      if (!active) return;
      const key = (e.key || "").toLowerCase();
      if (key === "c") {
        e.preventDefault();
        openQuickTag("client", active);
      } else if (key === "p") {
        e.preventDefault();
        openQuickTag("period", active);
      } else if (key === "t") {
        e.preventDefault();
        openQuickTag("task", active);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, openQuickTag]);

  // Load periods for the active client; load tasks for the active period.
  useEffect(() => {
    if (!active?.client_id) { setPeriods([]); return; }
    api.get(`/periods?client_id=${active.client_id}`).then((r) => setPeriods(r.data || [])).catch(() => setPeriods([]));
  }, [active?.client_id]);
  useEffect(() => {
    if (!active?.period_id) { setTasks([]); return; }
    api.get(`/periods/${active.period_id}/tasks`).then((r) => setTasks(r.data || [])).catch(() => setTasks([]));
  }, [active?.period_id]);

  // Iter 66b — Build a Gmail-style quote block for the editor so the user
  // sees the parent chain INLINE while composing (matching native Gmail).
  // Backend stitching is now a fallback only — when the editor's body_html
  // already contains `gmail_quote_container`, the backend skips re-stitching.
  const buildQuoteBlock = (parent, mode) => {
    if (!parent) return "";
    const parentFromName = parent.from_name || parent.from_email || "Unknown";
    const parentFromEmail = parent.from_email || "";
    const parentTo = parent.to_email || "";
    const parentSubject = parent.subject || "";
    let parentSent = "";
    try {
      if (parent.received_at) {
        const d = new Date(parent.received_at);
        // Iter 93i — Match native Gmail Web's EXACT date format because
        // Gmail's server-side forwarded-block scanner is regex-strict.
        // The reference native format (verified against a 2025 forward
        // in this user's own Sent folder that DID trigger the
        // "Forwarded" indicator) is:
        //   `Tue, 30 Dec 2025 at 10:10`
        //   → <weekday>, <day> <month> <year> at <HH>:<MM>
        // Previously we used `.toLocaleString` + `.replace(",", "")`
        // which produced `Fri 19 Jun 2026, 15:25` (no weekday comma,
        // comma instead of "at"). That mismatched the scanner regex
        // and Gmail refused to mark the parent as forwarded.
        const weekday = d.toLocaleString("en-GB", { weekday: "short" });
        const day = d.toLocaleString("en-GB", { day: "numeric" });
        const month = d.toLocaleString("en-GB", { month: "short" });
        const year = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        parentSent = `${weekday}, ${day} ${month} ${year} at ${hh}:${mm}`;
      }
    } catch (_e) { /* ignore */ }
    const parentBodyHtml = parent.body_html
      || (parent.body || "").replace(/\n/g, "<br>");
    const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    let meta;
    if (mode === "forward") {
      // Iter 93j — Match native Gmail Web's forward HTML structure
      // byte-for-byte. Reference fixture: 2025 native forward in this
      // user's Sent folder that DID trigger the indicator.
      // Differences from the previous (broken) Close format:
      //   * outer div carries BOTH classes: gmail_quote gmail_quote_container
      //   * meta div is `class="gmail_attr"` with `dir="ltr"`
      //   * labels are plain text ("From: ..." not "<b>From:</b> ...");
      //     only the sender NAME is bolded via <strong>
      //   * NO <blockquote> wrapper around the parent body — Gmail
      //     reserves blockquote for replies; forwards inline the
      //     parent body directly into the container
      meta = (
        '<div dir="ltr" class="gmail_attr">' +
        '---------- Forwarded message ---------<br>' +
        `From: <strong class="gmail_sendername" dir="auto">${esc(parentFromName)}</strong> <span dir="auto">&lt;<a href="mailto:${esc(parentFromEmail)}">${esc(parentFromEmail)}</a>&gt;</span><br>` +
        `Date: ${esc(parentSent)}<br>` +
        `Subject: ${esc(parentSubject)}<br>` +
        `To: ${esc(parentTo)}<br>` +
        '</div>'
      );
    } else {
      meta = (
        `<div class="gmail_attr">On ${esc(parentSent)}, ${esc(parentFromName)} ` +
        `&lt;<a href="mailto:${esc(parentFromEmail)}">${esc(parentFromEmail)}</a>&gt; wrote:</div>`
      );
    }
    return (
      mode === "forward"
        // Iter 93j — Forwards use the native Gmail HTML shape:
        //   <div class="gmail_quote gmail_quote_container">
        //     <div class="gmail_attr">...meta block...</div>
        //     <br><br>
        //     [parent body inline — NO blockquote]
        //   </div>
        // Replies keep the existing blockquote wrapper (the curved-
        // border quote style is what makes the curved-arrow REPLIED
        // indicator fire and is what native uses for replies).
        ? '<div class="gmail_quote gmail_quote_container">' +
          meta +
          '<br><br>' +
          parentBodyHtml +
          '</div>'
        : '<div class="gmail_quote_container">' +
          meta +
          '<blockquote class="gmail_quote" style="margin:0px 0px 0px 0.8ex;border-left:1px solid #ccc;padding-left:1ex">' +
          parentBodyHtml +
          '</blockquote></div>'
    );
  };

  // Iter 70 — RFC 5322 reply helpers.
  //
  // splitEmails: parse a CSV/semicolon/<bracket> address list into clean
  // `foo@bar.com` items, preserving any "Name <addr>" display formatting.
  //
  // Iter 110p — Delegates to the shared `splitAddresses` parser which
  // respects BOTH "double-quoted display names" and <angle-bracket
  // addresses>. Previously a naïve regex split on commas inside the
  // Outlook "Last, First" name convention, producing red broken chips
  // on Reply-All in production.
  const splitEmails = (raw) => splitAddresses(raw);
  // emailAddr: extract just the address portion of `Name <addr>` or `addr`.
  const emailAddr = (s) => {
    if (!s) return "";
    const m = String(s).match(/<([^>]+)>/);
    return (m ? m[1] : s).trim().toLowerCase();
  };
  const dedupAddrs = (list, excludeSet) => {
    const seen = new Set();
    const out = [];
    for (const item of list) {
      const k = emailAddr(item);
      if (!k || seen.has(k) || excludeSet.has(k)) continue;
      seen.add(k);
      out.push(item);
    }
    return out;
  };

  const openReply = async (m, mode = "replyAll") => {
    if (!m) return;
    // Iter 82 — Capture the merged message returned by ensureBodyLoaded so
    // we always read attachments from the freshly-loaded row, never the
    // stale `messages` array sitting in this closure. The OR fallback
    // handles the case where the row was already hydrated.
    const loaded = await ensureBodyLoaded(m);
    const parent = loaded || messages.find((x) => x.message_id === m.message_id) || m;

    const meEmail = connectedGmailEmail; // already lowercase
    const fromAddr = emailAddr(parent.from_email);
    const isMyOwnOutbound = !!meEmail && fromAddr === meEmail;

    // Native Gmail behaviour:
    //  * "Reply" to a message I sent → To = original recipient list.
    //  * "Reply" to a message someone else sent → To = original sender.
    //  * "Reply All" includes the original sender + To/Cc, minus me, minus
    //    the new To (so I don't accidentally CC my own reply target).
    let toList = [];
    if (isMyOwnOutbound) {
      toList = splitEmails(parent.to_email);
    } else if (parent.from_email) {
      toList = [parent.from_email];
    }
    let ccList = [];
    if (mode === "replyAll") {
      const allRecipients = [
        ...splitEmails(parent.to_email),
        ...splitEmails(parent.cc_email),
      ];
      // Exclude: my own address, the new To, and anything already listed.
      const exclude = new Set([meEmail, ...toList.map(emailAddr)]);
      ccList = dedupAddrs(allRecipients, exclude);
      // Also strip my own address from the To list, just in case CC and
      // To both contained me.
      toList = dedupAddrs(toList, new Set([meEmail]));
    } else {
      toList = dedupAddrs(toList, new Set([meEmail]));
    }

    // Iter 93f — Build the quote block in the correct mode. Previously
    // this was hardcoded to "reply" so forwards inherited the reply
    // meta line ("On <date> <sender> wrote:") instead of the canonical
    // "---------- Forwarded message ---------" block. Gmail's server-
    // side index scanner uses that exact text marker to apply the
    // native "Forwarded" indicator arrow to the parent message —
    // without it, Gmail treats the send as a reply on a new thread
    // (the worst of both worlds) and never marks the parent.
    const quoteHtml = buildQuoteBlock(parent, mode === "forward" ? "forward" : "reply");
    // Iter 86 — Case-INSENSITIVE Re: detection so we don't double-prefix
    // ("Re: RE: Tax notice"). External clients send RE:, Re:, re: — all
    // are valid reply prefixes per RFC 5322.
    const subject = /^\s*re\s*:/i.test(parent.subject || "")
      ? parent.subject
      : `Re: ${parent.subject || ""}`;
    const base = {
      channel: m.channel || "email",
      client_id: m.client_id || "",
      body: "",
      body_html: "",
      quote_html: quoteHtml,
      original_message_id: m.message_id,
      compose_mode: "reply",
    };
    let session;
    if (mode === "forward") {
      // Iter 82 — Native Gmail forward MUST carry attachments through to
      // the composer. Each forwarded chip is a "ref" (Gmail message + part
      // id) — no base64 download in the browser. The backend hydrates the
      // bytes server-side at send time so a 20 MB PDF doesn't have to
      // round-trip via React. Skip inline CID images — they live inside
      // the quote HTML and are re-embedded automatically by Gmail when
      // the recipient renders the message.
      // Iter 110d — Use `content_id` as the truth signal instead of
      // `is_inline`. Some senders (iPhone Mail in particular) flag
      // legitimate file attachments as Content-Disposition: inline
      // even though they have no CID reference in the HTML body.
      // Those parts are real files that must be forwarded. We mirror
      // the backend's read-time filter so historic rows (which were
      // synced before iter 110d and still carry `is_inline=True`) are
      // also carried through.
      const carriedAttachments = (parent.attachments || [])
        .filter((a) => !a.content_id && a.attachment_id)
        .map((a) => ({
          filename: a.filename,
          mime_type: a.mime_type,
          size: a.size || 0,
          attachment_id: a.attachment_id,
          source_gmail_id: parent.gmail_id,
        }));
      session = { ...base,
        to: "",
        cc: "",
        subject: /^\s*fwd?\s*:/i.test(parent.subject || "") ? parent.subject : `Fwd: ${parent.subject || ""}`,
        thread_id: null,
        compose_mode: "forward",
        forward_attachments: carriedAttachments,
      };
    } else {
      session = { ...base,
        to: toList.join(", "),
        cc: ccList.join(", "),
        subject,
        thread_id: parent.thread_id || null,
      };
    }
    composeApi.open({
      initial: session,
      clientId: session.client_id,
      showClientPicker: true,
      title: session.subject || "New message",
      onSent: () => { load(); loadDrafts(); },
    });
  };

  // Iter 84 — Unified "Add to To-Do" flow. Replaces the previous double-
  // button setup where `addToTodo` did a silent insert and
  // `openTodoFromMessage` opened a dialog. Now there's ONE entry point —
  // the dialog (which lets the user edit title/notes/due/assignee).
  // Accepts an optional `m` arg so the right-click context menu can act on
  // a specific row without first switching the active selection.
  const openTodoFromMessage = (m = null) => {
    const target = m || active;
    if (!target) return;
    setTodo({
      title: target.subject || (target.body || "").slice(0, 80),
      notes: `From ${target.from_name || target.from_email}\n\n${target.body || ""}`.slice(0, 1000),
      due_date: "",
      assigned_user_id: me?.user_id || "",
    });
    setTodoOpen(true);
  };

  const saveTodo = async () => {
    if (!todo.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post("/todos", {
        title: todo.title, notes: todo.notes,
        source_kind: active?.channel === "chat" ? "chat" : "email",
        source_id: active?.message_id, client_id: active?.client_id || null,
        due_date: todo.due_date || null,
        assigned_user_id: todo.assigned_user_id || null,
      });
      const isMine = !todo.assigned_user_id || todo.assigned_user_id === me?.user_id;
      const target = users.find((u) => u.user_id === todo.assigned_user_id);
      toast.success(isMine ? "Added to your To-Do list" : `Assigned to ${target?.name || "team member"}`);
      setTodoOpen(false);
    } catch (e) { toast.error("Failed"); }
  };

  const FolderItem = ({ id, icon: Icon, label, count, color, indent = false }) => (
    <button
      onClick={() => { setFolder(id); setActiveId(null); }}
      data-testid={`folder-${id}`}
      className={`w-full flex items-center gap-3 ${indent ? "pl-12" : "pl-6"} pr-4 py-2 rounded-r-full text-sm transition-colors ${
        folder === id ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon className={`h-4 w-4 ${folder === id ? "text-red-600" : color || "text-slate-500"}`} strokeWidth={1.8}/>
      <span className="flex-1 text-left">{label}</span>
      {count > 0 && <span className={`text-[11px] tabular-nums ${folder === id ? "text-red-700" : "text-slate-500"}`}>{count}</span>}
    </button>
  );

  return (
    <div className="flex flex-col bg-white h-screen overflow-hidden" data-testid="triage-page">
      <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* LEFT FOLDER PANE */}
      <aside className="w-56 shrink-0 border-r border-slate-200 py-4 bg-white overflow-y-auto">
        <div className="px-4 mb-3">
          <Button
            data-testid="compose-button"
            onClick={() => composeApi.open({
              initial: { to: "", cc: "", subject: "", body_html: "", client_id: "" },
              showClientPicker: true,
              title: "New message",
              onSent: () => { load(); loadDrafts(); },
            })}
            className="w-full justify-start gap-3 h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-sm shadow-slate-200/60"
          >
            <Pencil className="h-4 w-4"/> Compose
          </Button>
        </div>
        <nav className="space-y-0.5 pr-2">
          <FolderItem id="inbox" icon={InboxIcon} label="Inbox" count={counts.inbox}/>
          <FolderItem id="starred" icon={Star} label="Starred" count={counts.starred}/>
          <FolderItem id="gmail:SNOOZED" icon={Clock} label="Snoozed"/>
          <FolderItem id="gmail:IMPORTANT" icon={Bookmark} label="Important"/>
          <FolderItem id="sent" icon={Send} label="Sent" count={counts.sent}/>
          <FolderItem id="scheduled" icon={Clock} label="Scheduled" count={scheduledSends.length}/>
          <FolderItem id="drafts" icon={FileText} label="Drafts" count={drafts.length}/>
          <FolderItem id="archived" icon={Archive} label="Archived" count={counts.archived}/>
          <FolderItem id="all" icon={Mail} label="All mail"/>
          <FolderItem id="gmail:SPAM" icon={AlertTriangle} label="Spam"/>
          <FolderItem id="gmail:TRASH" icon={Trash2} label="Bin"/>
          {/* Iter 110ac — Gmail Categories mirror. Uses the CATEGORY_*
              system labels Gmail auto-applies (Personal, Promotions,
              Social, Updates, Forums, Purchases). Collapsible so the
              sidebar stays tidy for users who don't use categories. */}
          <CategoriesGroup active={folder} onPick={(cat) => { setFolder(`gmail:${cat}`); setActiveId(null); }} />
        </nav>

        {/* Gmail labels tree — hierarchical (parses Gmail's `/` path) */}
        {gmailConnected && gmailLabels.length > 0 && (
          <GmailLabelTree
            labels={gmailLabels}
            active={folder}
            onPick={(id) => { setFolder(id ? `gmail:${id}` : "inbox"); setActiveId(null); }}
          />
        )}

        <GmailConnect/>
      </aside>

      {/* MAIN COLUMN */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Search bar — Iter 88: inline Gmail-native advanced search dropdown.
            Clicking the input or pressing Cmd/Ctrl-K opens the dropdown
            anchored directly below. Submitting compiles to Gmail operator
            syntax (`from:`, `to:`, `subject:`, `has:attachment`, `newer_than:`,
            `before:`, `in:`) and feeds the existing inbox loader, so results
            appear in this same list and clicking a row opens the side preview. */}
        <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-3">
          <InboxSearchDropdown
            value={search}
            inputClassName="max-w-3xl flex-1"
            onChange={setSearch}
            onSubmit={(q) => setSearch(q)}
            onClear={() => setSearch("")}
          />
          <div className="text-xs text-slate-400 tabular-nums">{messages.length} message{messages.length === 1 ? "" : "s"}</div>
        </div>
        <InboxSearchChips
          value={search}
          onChange={setSearch}
          onClear={() => setSearch("")}
        />

        {/* Toolbar */}
        <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-1 bg-white">
          <Checkbox
            data-testid="select-all"
            checked={messages.length > 0 && selected.size === messages.length}
            onCheckedChange={selectAll}
            className="mx-2"
          />
          {selected.size > 0 && (
            <>
              <span className="text-xs text-slate-500 ml-1 mr-2">{selected.size} selected</span>
              <Button size="sm" variant="ghost" onClick={bulkArchive} data-testid="bulk-archive" title="Archive selected"><Archive className="h-4 w-4"/></Button>
              <Button size="sm" variant="ghost" onClick={() => bulkMarkRead("read")} data-testid="bulk-read" title="Mark as read">Mark read</Button>
              <Button size="sm" variant="ghost" onClick={() => bulkMarkRead("unread")} data-testid="bulk-unread" title="Mark as unread">Mark unread</Button>
              <Button size="sm" variant="ghost" onClick={bulkDelete} data-testid="bulk-delete" title="Delete selected" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"><Trash2 className="h-4 w-4"/></Button>
            </>
          )}
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
            {/* Karbon-style List/Split toggle */}
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden mr-2" data-testid="view-mode-toggle">
              <button
                type="button"
                onClick={() => setViewModePersist("list")}
                data-testid="view-mode-list"
                className={`px-2.5 py-1 text-[11px] transition-colors ${viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              >List</button>
              <button
                type="button"
                onClick={() => setViewModePersist("split")}
                data-testid="view-mode-split"
                className={`px-2.5 py-1 text-[11px] transition-colors ${viewMode === "split" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              >Split</button>
            </div>
            <span className="capitalize hidden sm:inline">{folder === "all" ? "All mail" : (isGmailFolder ? (gmailLabels.find((L) => L.id === gmailFolderId)?.name || "Label") : folder)}</span>
            {folder === "inbox" && <span className="text-slate-400">· {counts.inbox} unread</span>}
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* MESSAGE LIST — width depends on viewMode (list = full, split = ~40%) */}
          <div className={`${active && viewMode === "split" ? "hidden xl:block xl:w-[40%]" : (active && viewMode === "list" ? "hidden" : "flex-1")} border-r border-slate-200 overflow-y-auto`}>
            {folder === "scheduled" ? (
              scheduledSends.length === 0 ? (
                <div className="p-16 text-center text-sm text-slate-500" data-testid="scheduled-empty">
                  <Clock className="h-8 w-8 text-slate-300 mx-auto mb-3"/>
                  <div>No scheduled sends.</div>
                  <div className="text-xs text-slate-400 mt-1">Use the <span className="font-medium">Send</span> dropdown in Compose to schedule.</div>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="scheduled-list">
                  {scheduledSends.map((s) => {
                    const when = new Date(s.scheduled_at);
                    return (
                      <li key={s.schedule_id} className="px-4 py-3 hover:bg-slate-50 group" data-testid={`scheduled-row-${s.schedule_id}`}>
                        <div className="flex items-start gap-3">
                          <Clock className="h-4 w-4 text-amber-500 shrink-0 mt-0.5"/>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">
                              Sending {when.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </div>
                            <div className="text-sm font-medium text-slate-900 truncate">{s.subject || "(no subject)"}</div>
                            <div className="text-xs text-slate-500 truncate">To {s.to}{s.cc ? ` · Cc ${s.cc}` : ""}</div>
                          </div>
                          <button
                            onClick={() => cancelScheduled(s.schedule_id)}
                            className="text-[11px] text-rose-700 hover:underline shrink-0"
                            data-testid={`scheduled-cancel-${s.schedule_id}`}
                          >Cancel</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )
            ) : folder === "drafts" ? (
              drafts.length === 0 ? (
                <div className="p-16 text-center text-sm text-slate-500" data-testid="drafts-empty">
                  <FileText className="h-8 w-8 text-slate-300 mx-auto mb-3"/>
                  <div>No saved drafts.</div>
                  <div className="text-xs text-slate-400 mt-1">Drafts auto-save 2 seconds after you stop typing — minimise the composer to come back later.</div>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="drafts-list">
                  {drafts.map((d) => {
                    const when = new Date(d.updated_at);
                    return (
                      <li key={d.draft_id} className="px-4 py-3 hover:bg-slate-50 group" data-testid={`drafts-row-${d.draft_id}`}>
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            className="flex-1 min-w-0 flex items-start gap-3 text-left"
                            onClick={() => {
                              // Restore the draft into a NEW compose session.
                              composeApi.open({
                                initial: {
                                  draft_id: d.draft_id,
                                  to: d.to_email || "",
                                  cc: d.cc || "",
                                  bcc: d.bcc || "",
                                  subject: d.subject || "",
                                  body_html: d.body_html || "",
                                  thread_id: d.thread_id || null,
                                  in_reply_to: d.in_reply_to || "",
                                  references: d.references || "",
                                  original_message_id: d.original_message_id || "",
                                  compose_mode: d.compose_mode || "",
                                  quote_html: d.quote_html || "",
                                  task_id: d.task_id || null,
                                  client_id: d.client_id || "",
                                },
                                clientId: d.client_id || "",
                                showClientPicker: true,
                                title: d.subject || "New message",
                                onSent: () => { load(); loadDrafts(); },
                              });
                            }}
                            data-testid={`drafts-open-${d.draft_id}`}
                          >
                            <FileText className="h-4 w-4 text-slate-400 shrink-0 mt-0.5"/>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] uppercase tracking-wider text-slate-500">
                                {when.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                              </div>
                              <div className="text-sm font-medium text-slate-900 truncate">{d.subject || "(no subject)"}</div>
                              <div className="text-xs text-slate-500 truncate">To {d.to_email || "(no recipient)"} — {((d.body_text || "").slice(0, 60)).trim() || "(empty)"}</div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm("Discard this draft?")) return;
                              try { await api.delete(`/drafts/${d.draft_id}`); toast.success("Draft discarded"); loadDrafts(); }
                              catch { toast.error("Failed"); }
                            }}
                            className="text-[11px] text-rose-700 hover:underline shrink-0"
                            data-testid={`drafts-delete-${d.draft_id}`}
                          >Discard</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )
            ) : threadGroups.length === 0 ? (
              messagesLoading ? (
                // Iter 69 — show subtle skeleton instead of "No messages in
                // this folder." flash while the first request is in flight.
                <div className="px-3 py-2 space-y-2" data-testid="inbox-loading">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-2 border-b border-slate-100 pb-2 animate-pulse">
                      <div className="h-4 w-4 rounded bg-slate-200 mt-0.5"/>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="h-3 bg-slate-200 rounded w-1/3"/>
                        <div className="h-3 bg-slate-100 rounded w-3/4"/>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-16 text-center text-sm text-slate-500" data-testid="inbox-empty">
                  <InboxIcon className="h-8 w-8 text-slate-300 mx-auto mb-3"/>
                  <div>No messages in this folder.</div>
                  <div className="text-xs text-slate-400 mt-1">Try the <span className="font-medium">Compose</span> or <span className="font-medium">Import message</span> button.</div>
                </div>
              )
            ) : (
              <ul>
                {threadGroups.map((m, rowIndex) => {
                  const unread = m._thread_unread || m.status === "unread";
                  const threadCount = m._thread_count || 1;
                  const senderName = m.direction === "outbound" ? (m.to_email || "(no recipient)") : (m.from_name || m.from_email || "Unknown");
                  // Iter 85 — Show the "replied / forwarded" indicators
                  // based on the LOCAL action flags the backend stamps on
                  // the parent row (replied_at / forwarded_at / last_action).
                  // The old subject-prefix heuristic mis-fired: it lit up
                  // inbound "Re:" emails as if the user had personally
                  // replied to them, and it never fired on outbound replies
                  // the user authored.
                  const isReply = !!m.replied_at || m.last_action === "reply";
                  const isFwd   = !!m.forwarded_at || m.last_action === "forward";
                  return (
                    <ContextMenu key={m.message_id}>
                      <ContextMenuTrigger asChild>
                    <li
                      onClick={() => openMessage(m)}
                      data-testid={`message-row-${m.message_id}`}
                      className={`group flex items-start gap-2.5 px-3.5 py-3 border-b border-slate-100 cursor-pointer transition-colors ${
                        activeId === m.message_id ? "bg-blue-50/70" : selected.has(m.message_id) ? "bg-amber-50" : (unread ? "bg-white hover:bg-slate-50" : "bg-slate-50/40 hover:bg-white")
                      }`}
                    >
                      <div onClick={(e) => { e.stopPropagation(); handleRowSelect(m.message_id, rowIndex, e); }} className="mt-0.5">
                        <Checkbox checked={selected.has(m.message_id)} onCheckedChange={() => {}} data-testid={`select-${m.message_id}`}/>
                      </div>
                      <button onClick={(e) => toggleStar(m, e)} data-testid={`star-${m.message_id}`} className="text-slate-300 hover:text-amber-500 shrink-0 mt-0.5">
                        <Star className={`h-4 w-4 ${m.starred ? "fill-amber-400 text-amber-400" : ""}`}/>
                      </button>
                      {/* FROM column — fixed width, sender + client chip stacked.
                          Iter 110q — Sender always rendered in slate-900 (was
                          slate-700 for read). Strong sender/subject vs lighter
                          snippet improves the visual hierarchy. */}
                      <div className="w-[180px] shrink-0 min-w-0 text-slate-900">
                        <div className={`truncate text-sm ${unread ? "font-semibold" : "font-medium"}`}>
                          {senderName}
                          {m.direction === "outbound" && <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-1 font-normal">Sent</span>}
                        </div>
                        {m.client_name && (
                          <div className="mt-1 flex items-center gap-1 min-w-0">
                            <span className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded-sm inline-block truncate max-w-full" title={m.client_alias ? `${m.client_name} (${m.client_alias})` : m.client_name}>{m.client_name}</span>
                            {m.client_alias && (
                              <span className="text-[10px] lowercase text-slate-500 truncate" title={m.client_alias}>{m.client_alias}</span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* SUBJECT + body preview — flex-1 takes the rest */}
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        <div className="truncate text-sm flex items-center gap-1.5">
                          {isReply && (
                            <span
                              className="inline-flex items-center text-[9px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 py-0 rounded-sm shrink-0 leading-none"
                              title="Replied — Gmail label: Close/Replied"
                            >Replied</span>
                          )}
                          {isFwd && (
                            <span
                              className="inline-flex items-center text-[9px] font-medium bg-sky-50 text-sky-700 border border-sky-200 px-1 py-0 rounded-sm shrink-0 leading-none"
                              title="Forwarded — Gmail label: Close/Forwarded"
                            >Forwarded</span>
                          )}
                          {/* Iter 110q — Subject always slate-900 (was slate-700 for
                              read). Keeps sender + subject as the high-contrast pair. */}
                          <span className={`text-slate-900 ${unread ? "font-semibold" : "font-medium"}`}>{m.subject || "(no subject)"}</span>
                          {threadCount > 1 && (
                            <span
                              className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 px-1 py-0.5 rounded-sm shrink-0 tabular-nums"
                              title={`${threadCount} messages in this thread`}
                              data-testid={`thread-count-${m.message_id}`}
                            >
                              {threadCount}
                            </span>
                          )}
                          {/* Iter 110q — Snippet:
                              (a) HTML entities decoded so `It&#39;s` shows as `It's`.
                              (b) text-slate-400 (was slate-500) — lighter snippet
                                  creates clearer visual hierarchy under the sender
                                  + subject pair. `font-normal` even when row is
                                  unread so the subject pops harder. */}
                          <span className="text-slate-400 font-normal truncate"> — {decodeHtmlEntities((m.snippet || m.body || "")).replace(/\s+/g, " ").slice(0, 140)}</span>
                        </div>
                        {/* Tiny chip cluster (no client chip — moved under sender) */}
                        <div className="shrink-0 flex items-center gap-1">
                          {m.channel === "chat" ? <MessageSquare className="h-3 w-3 text-purple-500"/> : null}
                          {m.assigned_user_id === me?.user_id && (m.labels || []).includes("mention") && (
                            <AtSign className="h-3 w-3 text-amber-600" title="You were mentioned"/>
                          )}
                          {m.task_code && (
                            <span className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-sm" title={m.task_name || ""} data-testid={`row-task-${m.message_id}`}>
                              [{m.task_code}]
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Quick-label buttons removed in iter 43 — todo/done/important
                          tags are no longer part of the email model. */}
                      <div className="shrink-0 text-[11px] text-slate-500 tabular-nums ml-2 min-w-[120px] text-right">
                        {formatTime(m.received_at)}
                      </div>
                    </li>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="w-44" data-testid={`row-context-${m.message_id}`}>
                        <ContextMenuItem onSelect={() => openReply(m, "replyAll")} data-testid={`ctx-reply-all-${m.message_id}`}>
                          <ReplyAll className="h-3.5 w-3.5 mr-2"/> Reply all
                        </ContextMenuItem>
                        <ContextMenuItem onSelect={() => openReply(m, "reply")} data-testid={`ctx-reply-${m.message_id}`}>
                          <Reply className="h-3.5 w-3.5 mr-2"/> Reply (sender only)
                        </ContextMenuItem>
                        <ContextMenuItem onSelect={() => openReply(m, "forward")} data-testid={`ctx-forward-${m.message_id}`}>
                          <Forward className="h-3.5 w-3.5 mr-2"/> Forward
                        </ContextMenuItem>
                        <ContextMenuItem onSelect={() => openTodoFromMessage(m)} data-testid={`ctx-todo-${m.message_id}`}>
                          <ListTodo className="h-3.5 w-3.5 mr-2"/> Add to To-Do list
                        </ContextMenuItem>
                        <ContextMenuSeparator/>
                        <ContextMenuItem onSelect={() => openQuickTag("client", m)} data-testid={`ctx-tag-client-${m.message_id}`}>
                          <Tag className="h-3.5 w-3.5 mr-2"/> Tag client…
                          <span className="ml-auto text-[10px] text-slate-400 font-mono">c</span>
                        </ContextMenuItem>
                        <ContextMenuItem
                          onSelect={() => openQuickTag("period", m)}
                          disabled={!m.client_id}
                          data-testid={`ctx-tag-period-${m.message_id}`}
                        >
                          <Tag className="h-3.5 w-3.5 mr-2"/> Tag close…
                          <span className="ml-auto text-[10px] text-slate-400 font-mono">p</span>
                        </ContextMenuItem>
                        <ContextMenuItem
                          onSelect={() => openQuickTag("task", m)}
                          disabled={!m.period_id}
                          data-testid={`ctx-tag-task-${m.message_id}`}
                        >
                          <Tag className="h-3.5 w-3.5 mr-2"/> Tag task…
                          <span className="ml-auto text-[10px] text-slate-400 font-mono">t</span>
                        </ContextMenuItem>
                        {/* Iter 108 — Gmail labels sub-menu. Lets the user
                            apply / remove any of their Gmail labels on a
                            row without opening it. Disabled when Gmail is
                            not connected or the message hasn't been
                            mirrored to Gmail yet (no gmail_id). */}
                        <RowLabelsSubmenu
                          message={m}
                          labels={gmailLabels}
                          gmailConnected={gmailConnected}
                          onToggle={toggleRowLabel}
                        />
                        <ContextMenuSeparator/>
                        <ContextMenuItem onSelect={() => archiveMsg(m)} data-testid={`ctx-archive-${m.message_id}`}>
                          <Archive className="h-3.5 w-3.5 mr-2"/> {m.status === "archived" ? "Move to Inbox" : "Archive"}
                        </ContextMenuItem>
                        <ContextMenuItem onSelect={() => deleteMsg(m)} data-testid={`ctx-delete-${m.message_id}`} className="text-red-600 focus:text-red-700">
                          <Trash2 className="h-3.5 w-3.5 mr-2"/> Delete
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  );
                })}
              </ul>
            )}
          </div>

          {/* DETAIL PANE
              Iter 110ab — `key={activeId}` on the outer container forces a
              full remount when the user switches messages. Without this,
              React re-uses the same DOM node for the body's
              `dangerouslySetInnerHTML` div and — under certain race conditions
              between the lazy body fetch (`ensureBodyLoaded`) and the
              PATCH-based row rewrites in `linkClient` / `linkPeriod` /
              `linkTask` / `assignUser` (which replace the entire row with
              `r.data`, wiping the freshly-loaded `body_html_quoted` field
              and repopulating `body_html` with the raw unsplit HTML) — the
              previously rendered innerHTML could visually persist. Fully
              remounting the subtree guarantees the body element is
              re-created from scratch for every message the user opens. */}
          {active && (
            <div key={active.message_id} className="flex-1 overflow-y-auto bg-white">
              {/* Detail toolbar */}
              <div className="px-5 py-2.5 border-b border-slate-200 flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => setActiveId(null)} data-testid="close-message" className="text-slate-500"><ChevronLeft className="h-4 w-4"/></Button>
                <Button size="sm" variant="ghost" onClick={() => archiveMsg(active)} data-testid="archive-button" title={active.status === "archived" ? "Move to Inbox" : "Archive"}>
                  {active.status === "archived" ? <ArchiveX className="h-4 w-4"/> : <Archive className="h-4 w-4"/>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteMsg(active)} data-testid="delete-message" title="Delete"><Trash2 className="h-4 w-4"/></Button>
                <Button size="sm" variant="ghost" onClick={openTodoFromMessage} data-testid="add-todo-button" title="Add to To-Do list" className="text-slate-600 h-7 px-2"><ListTodo className="h-4 w-4"/></Button>
                <div className="border-l border-slate-200 mx-1 h-5"/>
                {/* Iter 70 — Reply defaults to Reply-All. Primary button uses
                    ReplyAll icon since that's the action. A secondary button
                    keeps "Reply sender only" available for the edge case. */}
                <Button size="sm" variant="ghost" onClick={() => openReply(active, "replyAll")} data-testid="reply-button-top" title="Reply (defaults to Reply All)" className="text-slate-600 h-7 px-2">
                  <ReplyAll className="h-4 w-4"/>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openReply(active, "reply")} data-testid="reply-sender-only-button-top" title="Reply to sender only" className="text-slate-600 h-7 px-2">
                  <Reply className="h-4 w-4"/>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openReply(active, "forward")} data-testid="forward-button-top" title="Forward" className="text-slate-600 h-7 px-2">
                  <Forward className="h-4 w-4"/>
                </Button>
                <div className="ml-auto flex items-center gap-2 text-xs flex-wrap">
                  <SearchableSelect
                    value={active.client_id || ""}
                    onValueChange={(v) => linkClient(v || "none")}
                    options={clients.map((c) => ({ value: c.client_id, label: c.alias ? `${c.name} (${c.alias})` : c.name }))}
                    emptyLabel="No client"
                    testId="link-client"
                    className="w-40"
                  />
                  {active.client_id && (
                    <SearchableSelect
                      value={active.period_id || ""}
                      onValueChange={(v) => linkPeriod(v || "none")}
                      options={periods.map((p) => ({ value: p.period_id, label: p.period_label }))}
                      placeholder="Link close"
                      emptyLabel="No close"
                      testId="link-period"
                      className="w-36"
                    />
                  )}
                  {active.period_id && (
                    <SearchableSelect
                      value={active.task_id || ""}
                      onValueChange={(v) => linkTask(v || "none")}
                      options={tasks.map((t) => ({ value: t.task_id, label: t.name }))}
                      placeholder="Link task"
                      emptyLabel="No task"
                      testId="link-task"
                      className="w-40"
                    />
                  )}
                  <SearchableSelect
                    value={active.assigned_user_id || ""}
                    onValueChange={(v) => assignUser(v || "none")}
                    options={users.map((u) => ({ value: u.user_id, label: u.name }))}
                    placeholder="Assign"
                    emptyLabel="Unassigned"
                    testId="assign-user"
                    className="w-36"
                  />
                </div>
              </div>

              {/* Message header */}
              <div className="px-6 pt-6 pb-3">
                <div className="flex items-baseline justify-between gap-3 mb-4">
                  <h2 className="font-display text-2xl tracking-tight text-slate-900 leading-tight">{active.subject || "(no subject)"}</h2>
                  <button onClick={() => toggleStar(active)} data-testid="star-active" className="text-slate-300 hover:text-amber-500 shrink-0"><Star className={`h-5 w-5 ${active.starred ? "fill-amber-400 text-amber-400" : ""}`}/></button>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium text-slate-900">{active.direction === "outbound" ? me?.name : (active.from_name || active.from_email)}</span>
                      <span className="text-slate-500 ml-2 text-xs">&lt;{active.direction === "outbound" ? me?.email : active.from_email}&gt;</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      to <RecipientsCollapsible rawList={active.to_email} direction={active.direction} myEmail={me?.email} testIdPrefix="active-to" />
                      {(() => {
                        // Iter 93b — inline CC, same visual treatment as the
                        // "to …" line. No chips: matches Karbon / native Gmail
                        // detail header and stays out of the way when long.
                        // Iter 110w — chevron collapse when >3 cc recipients.
                        const ccRaw = (active.cc_email || "").trim();
                        if (!ccRaw) return null;
                        return (
                          <>
                            <span className="mx-1.5">·</span>
                            cc <span data-testid="cc-inline"><RecipientsCollapsible rawList={ccRaw} direction={active.direction} myEmail={me?.email} testIdPrefix="active-cc" /></span>
                          </>
                        );
                      })()}
                      <span className="mx-1.5">·</span>
                      <Clock className="h-3 w-3 inline -mt-0.5"/> <span className="tabular-nums">{formatTime(active.received_at)}</span>
                    </div>
                  </div>
                  <MessageLabelPicker
                    message={active}
                    labels={gmailLabels}
                    gmailConnected={gmailConnected}
                    onUpdated={(newIds) => {
                      // `active` is derived (useMemo) — patch the underlying
                      // messages array so the recomputed value picks up the
                      // new label set. (Pre-existing bug: `setActive` was
                      // never defined; fix-tagged Iter 71.)
                      setMessages((prev) => prev.map((m) =>
                        m.message_id === active?.message_id
                          ? { ...m, gmail_label_ids: newIds }
                          : m
                      ));
                      load();
                    }}
                  />
                </div>
              </div>

              <div className="px-6 pb-6 space-y-3">
                {activeThread.length > 1 && (
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 -mb-1" data-testid="thread-chain-count">
                    {activeThread.length} messages in this conversation
                  </div>
                )}
                {activeThread.map((m, idx) => {
                  const isExpanded = expandedThreadIds.has(m.message_id);
                  const isLast = idx === activeThread.length - 1;
                  const senderName = m.direction === "outbound"
                    ? (me?.name || "You")
                    : (m.from_name || m.from_email || "Unknown");
                  if (activeThread.length === 1) {
                    // Single-message conversation — render flat (no chain UI).
                    // Iter 93k — Apply Front/Superhuman body styling spec:
                    // 14px / line-height 1.4 / #2c2c2c, max-width 65%.
                    const showQuote = expandedQuotes.has(m.message_id);
                    return (
                      <div key={m.message_id} className="max-w-[65%]" style={{ fontSize: 14, lineHeight: 1.4, color: "#2c2c2c" }}>
                        {m.body_html ? (
                          <div
                            data-testid="message-body-html"
                            className="gmail-body"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(m.body_html, HTML_SANITIZE_CONFIG),
                            }}
                          />
                        ) : (
                          <div className="whitespace-pre-wrap">{m.body}</div>
                        )}
                        {/* Iter 71 — Show trimmed content toggle (Gmail's "..." pill) */}
                        {m.body_html_quoted ? (
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => toggleQuote(m.message_id)}
                              data-testid={`toggle-quote-${m.message_id}`}
                              className="inline-flex items-center justify-center px-2 py-0.5 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-500 text-[11px] leading-none"
                              title={showQuote ? "Hide trimmed content" : "Show trimmed content"}
                            >...</button>
                            {showQuote && (
                              <div
                                data-testid={`quoted-body-${m.message_id}`}
                                className="mt-2 text-sm text-slate-600 leading-relaxed gmail-body border-l-2 border-slate-200 pl-3"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(m.body_html_quoted, HTML_SANITIZE_CONFIG),
                                }}
                              />
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                  return (
                    <div
                      key={m.message_id}
                      className={`border ${isLast ? "border-slate-200" : "border-slate-100"} rounded-md bg-white`}
                      data-testid={`thread-msg-${m.message_id}`}
                    >
                      {/* Header — always visible, click to collapse/expand.
                          Iter 93k — Front/Superhuman 40px summary bar when
                          collapsed: avatar + sender + snippet + timestamp +
                          action-type icon. Same min-height for the expanded
                          state so the click target is consistent. */}
                      <button
                        type="button"
                        onClick={() => toggleThreadMessage(m)}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-slate-50/60 transition-colors rounded-md min-h-[40px]"
                      >
                        <div className="h-7 w-7 shrink-0 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold flex items-center justify-center select-none">
                          {(senderName || "?").trim().charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-[13px] font-medium text-slate-900 truncate flex items-center gap-1.5">
                              {(() => {
                                // Iter 93m — Render the same chip style as
                                // Gmail's `Close/Replied` / `Close/Forwarded`
                                // labels so the lineage cue is visually
                                // identical across both apps.
                                const at = actionTypeOf(m);
                                if (at === "outbound_reply") {
                                  return (
                                    <span
                                      className="inline-flex items-center text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-sm shrink-0"
                                      data-testid="lineage-chip-replied"
                                      title="Replied — Gmail label: Close/Replied"
                                    >Replied</span>
                                  );
                                }
                                if (at === "outbound_forward") {
                                  return (
                                    <span
                                      className="inline-flex items-center text-[10px] font-medium bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded-sm shrink-0"
                                      data-testid="lineage-chip-forwarded"
                                      title="Forwarded — Gmail label: Close/Forwarded"
                                    >Forwarded</span>
                                  );
                                }
                                return null;
                              })()}
                              {senderName}
                            </span>
                            <span className="text-[11px] text-slate-500 tabular-nums shrink-0">{formatTime(m.received_at)}</span>
                          </div>
                          {!isExpanded && (
                            <div className="text-[12px] text-slate-500 truncate mt-0.5">{(m.body || "").replace(/\s+/g, " ").slice(0, 140)}</div>
                          )}
                          {isExpanded && (
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              to <RecipientsCollapsible rawList={m.to_email} direction={m.direction} myEmail={me?.email} testIdPrefix={`msg-${m.message_id}-to`} />
                            </div>
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                          {m.body_html === undefined && m.body === undefined ? (
                            <div className="text-xs text-slate-400 py-3">Loading…</div>
                          ) : m.body_html ? (
                            <>
                              <div
                                className="gmail-body max-w-[65%]"
                                style={{ fontSize: 14, lineHeight: 1.4, color: "#2c2c2c" }}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(m.body_html, HTML_SANITIZE_CONFIG),
                                }}
                              />
                              {m.body_html_quoted ? (
                                <div className="mt-3">
                                  <button
                                    type="button"
                                    onClick={() => toggleQuote(m.message_id)}
                                    data-testid={`toggle-quote-${m.message_id}`}
                                    className="inline-flex items-center justify-center px-2 py-0.5 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-500 text-[11px] leading-none"
                                    title={expandedQuotes.has(m.message_id) ? "Hide trimmed content" : "Show trimmed content"}
                                  >...</button>
                                  {expandedQuotes.has(m.message_id) && (
                                    <div
                                      data-testid={`quoted-body-${m.message_id}`}
                                      className="mt-2 text-sm text-slate-600 leading-relaxed gmail-body border-l-2 border-slate-200 pl-3"
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(m.body_html_quoted, HTML_SANITIZE_CONFIG),
                                      }}
                                    />
                                  )}
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <div className="whitespace-pre-wrap max-w-[65%]" style={{ fontSize: 14, lineHeight: 1.4, color: "#2c2c2c" }}>{m.body}</div>
                          )}
                          {/* Per-message attachments */}
                          {(m.attachments || []).length > 0 && (
                            <div className="mt-3">
                              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                                {m.attachments.length} attachment{m.attachments.length === 1 ? "" : "s"}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {m.attachments.map((a) => {
                                  const kb = a.size ? Math.round(a.size / 1024) : 0;
                                  return (
                                    <button
                                      type="button"
                                      key={a.attachment_id}
                                      onClick={() => setPreviewAttachment({ att: a, gmail_id: m.gmail_id })}
                                      data-testid={`thread-attachment-${a.attachment_id}`}
                                      className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 hover:border-slate-300 transition-colors max-w-xs"
                                      title="Preview"
                                    >
                                      <FileText className="h-3.5 w-3.5 text-slate-500"/>
                                      <span className="truncate">{a.filename}</span>
                                      {kb > 0 && <span className="text-slate-400 shrink-0">{kb} KB</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Per-message attachments now render INSIDE each thread
                    bubble (above) so they sit with the right author. The
                    old single-message attachment block is intentionally
                    removed in iter 55. For single-message conversations
                    we render the attachment list once below. */}
                {activeThread.length === 1 && (active.attachments || []).length > 0 && (
                  <div className="mt-4 pl-[52px]">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">{active.attachments.length} attachment{active.attachments.length === 1 ? "" : "s"}</div>
                    <div className="flex flex-wrap gap-2">
                      {active.attachments.map((a) => {
                        const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/gmail/attachment/${active.gmail_id}/${a.attachment_id}`;
                        const kb = a.size ? Math.round(a.size / 1024) : 0;
                        return (
                          <div
                            key={a.attachment_id}
                            data-testid={`attachment-${a.attachment_id}`}
                            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 hover:border-slate-300 transition-colors max-w-xs cursor-pointer"
                            onClick={() => setPreviewAttachment({ att: a, gmail_id: active.gmail_id })}
                            title="Preview"
                          >
                            <FileText className="h-4 w-4 text-slate-500 shrink-0"/>
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium text-slate-900">{a.filename}</div>
                              <div className="text-[10px] text-slate-500">{kb} KB · {a.mime_type}</div>
                            </div>
                            <a
                              href={downloadUrl}
                              download={a.filename}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`attachment-download-${a.attachment_id}`}
                              className="text-slate-400 hover:text-slate-700 shrink-0 p-1 rounded hover:bg-slate-200"
                              title="Download"
                            >
                              <Download className="h-3 w-3"/>
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Reply / Reply all / Forward — moved to top toolbar */}

              {/* Iter 40 — Internal notes panel retired (redundant with the
                  GChat per-thread sidebar + general comments). */}
            </div>
          )}
        </div>
      </div>

      {/* Iter 67 — In-app attachment previewer */}
      <AttachmentPreview
        open={!!previewAttachment}
        attachment={previewAttachment?.att}
        gmailId={previewAttachment?.gmail_id}
        onClose={() => setPreviewAttachment(null)}
      />


      {/* Import dialog */}
      {/* To-Do dialog */}
      <Dialog open={todoOpen} onOpenChange={setTodoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Add to a To-Do list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Title</Label><Input data-testid="todo-title" value={todo.title} onChange={(e) => setTodo({ ...todo, title: e.target.value })}/></div>
            <div><Label className="text-sm">Notes</Label><Textarea rows={4} value={todo.notes} onChange={(e) => setTodo({ ...todo, notes: e.target.value })}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Assign to</Label>
                <Select
                  value={todo.assigned_user_id || (me?.user_id || "")}
                  onValueChange={(v) => setTodo({ ...todo, assigned_user_id: v })}
                >
                  <SelectTrigger data-testid="todo-assignee"><SelectValue placeholder="Pick a team member"/></SelectTrigger>
                  <SelectContent>
                    {me && <SelectItem value={me.user_id} data-testid="todo-assignee-me">Me ({me.name})</SelectItem>}
                    {users.filter((u) => u.user_id !== me?.user_id).map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id} data-testid={`todo-assignee-${u.user_id}`}>
                        {u.name} <span className="text-slate-400">· {u.role}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Due date</Label><Input type="date" data-testid="todo-due" value={todo.due_date} onChange={(e) => setTodo({ ...todo, due_date: e.target.value })}/></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTodoOpen(false)}>Cancel</Button>
            <Button data-testid="save-todo" onClick={saveTodo} className="bg-slate-900 hover:bg-slate-800 text-white">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* (Iter 88) GmailSearchPalette removed; replaced by InboxSearchDropdown
          anchored to the inbox search bar above. */}

      {/* Iter 94 — Quick Tag picker (Client / Close / Task) driven by the
          context menu items and by c/p/t keyboard shortcuts. We re-derive
          the message from the live `messages` array so that `currentId`
          (and the "Clear current selection" affordance) stay in sync after
          a tag is applied — the snapshot captured by openQuickTag would
          otherwise lag one render. */}
      {(() => {
        const liveMsg = quickTag
          ? (messages.find((x) => x.message_id === quickTag.message?.message_id) || quickTag.message)
          : null;
        return (
      <QuickTagPicker
        open={!!quickTag}
        onOpenChange={(o) => { if (!o) setQuickTag(null); }}
        title={
          quickTag?.kind === "client" ? "Tag client" :
          quickTag?.kind === "period" ? "Tag close" :
          quickTag?.kind === "task" ? "Tag task" : ""
        }
        items={
          quickTag?.kind === "client"
            ? clients.map((c) => ({
                value: c.client_id,
                label: c.alias ? `${c.name} (${c.alias})` : c.name,
              }))
          : quickTag?.kind === "period"
            ? quickPeriods.map((p) => ({ value: p.period_id, label: p.period_label }))
          : quickTag?.kind === "task"
            ? quickTasks.map((t) => ({ value: t.task_id, label: t.name }))
          : []
        }
        currentId={
          quickTag?.kind === "client" ? (liveMsg?.client_id || null) :
          quickTag?.kind === "period" ? (liveMsg?.period_id || null) :
          quickTag?.kind === "task" ? (liveMsg?.task_id || null) : null
        }
        emptyHint={
          quickTag?.kind === "period"
            ? "No closes yet for this client. Create one from the client page."
          : quickTag?.kind === "task"
            ? "This close has no tasks yet."
          : "No clients yet."
        }
        onSelect={async (val) => {
          const m = liveMsg;
          if (!m) return;
          try {
            if (quickTag.kind === "client") await linkClient(val, m);
            else if (quickTag.kind === "period") await linkPeriod(val, m);
            else if (quickTag.kind === "task") await linkTask(val, m);
            toast.success(
              quickTag.kind === "client" ? "Client tagged" :
              quickTag.kind === "period" ? "Close tagged" : "Task tagged"
            );
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't tag");
          }
        }}
        onClear={async () => {
          const m = liveMsg;
          if (!m) return;
          try {
            if (quickTag.kind === "client") await linkClient("none", m);
            else if (quickTag.kind === "period") await linkPeriod("none", m);
            else if (quickTag.kind === "task") await linkTask("none", m);
            toast.success("Tag cleared");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clear");
          }
        }}
        testId={`quick-tag-${quickTag?.kind || "none"}`}
      />
        );
      })()}
    </div>
    </div>
  );
}

// Shared tokenizer used by both `formatRecipientsForHeader` inside the main
// component and `<RecipientsCollapsible>` below. Module-scoped so both call
// sites reference the same function without prop-drilling.
function _labelsForHeader(rawList, direction, myEmailRaw) {
  const tokens = splitAddresses(rawList || "");
  const myEmail = (myEmailRaw || "").toLowerCase();
  return tokens.map((tok) => {
    const m = tok.match(/^(.*?)<([^>]+)>\s*$/);
    const email = m ? m[2].trim() : tok.trim();
    let name = m ? m[1].trim() : "";
    if (name.length >= 2 && name.startsWith('"') && name.endsWith('"')) {
      name = name.slice(1, -1).trim();
    }
    if (email.toLowerCase() === myEmail) return "me";
    return name || email;
  });
}

// -----------------------------------------------------------------------------
// RecipientsCollapsible (Iter 110w)
// -----------------------------------------------------------------------------
// Inbox header recipient renderer with a Gmail-style "+N more" chevron for
// long TO/CC lists. Prevents the header row from ballooning to a full line
// on emails addressed to 10-20 people (a common pattern for firm-wide
// announcements and payroll cc-alls). Threshold: 3 tokens visible by default.
//
// Props:
//   - rawList:   the raw address string (e.g. from `active.to_email`)
//   - direction: "inbound" | "outbound" — used only for the empty-list fallback
//   - myEmail:   used to collapse "you" → "me" (Gmail parity)
//   - testIdPrefix: for data-testid ("to" or "cc")
function RecipientsCollapsible({ rawList, direction, myEmail, testIdPrefix = "to" }) {
  const [expanded, setExpanded] = useState(false);
  const labels = _labelsForHeader(rawList, direction, myEmail);
  if (labels.length === 0) {
    return <span className="text-slate-700">{direction === "outbound" ? "(no recipient)" : "me"}</span>;
  }
  const THRESHOLD = 3;
  if (labels.length <= THRESHOLD || expanded) {
    return (
      <span className="text-slate-700" data-testid={`${testIdPrefix}-expanded`}>
        {labels.join(", ")}
        {labels.length > THRESHOLD && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
            className="ml-1.5 inline-flex items-center gap-0.5 text-slate-400 hover:text-slate-700 align-baseline"
            aria-label="Collapse recipients"
            data-testid={`${testIdPrefix}-collapse-btn`}
          >
            <ChevronDown className="h-3 w-3 rotate-180"/>
          </button>
        )}
      </span>
    );
  }
  const visible = labels.slice(0, THRESHOLD);
  const hidden = labels.length - THRESHOLD;
  return (
    <span className="text-slate-700" data-testid={`${testIdPrefix}-collapsed`}>
      {visible.join(", ")}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
        className="ml-1.5 inline-flex items-center gap-0.5 text-slate-500 hover:text-slate-800 text-[11px] font-medium align-baseline"
        title={`Show all ${labels.length} recipients`}
        data-testid={`${testIdPrefix}-expand-btn`}
      >
        +{hidden} more
        <ChevronDown className="h-3 w-3"/>
      </button>
    </span>
  );
}

